import filesize from 'filesize';
import type { UploaderRestrictions } from '../components/machines/universal-media-uploader-types';

// Default restriction values
const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const DEFAULT_MAX_NUMBER_OF_FILES = 1;

/**
 * Formats Uppy restriction error messages into user-friendly text.
 *
 * Since Uppy doesn't expose the specific restriction violation type directly,
 * this function uses string matching on the error message to detect the type
 * of violation and format an appropriate user-friendly message.
 *
 * @param error - The error object from Uppy's restriction-failed event
 * @param restrictions - The restrictions configuration object
 * @returns A formatted, user-friendly error message
 *
 * @example
 * ```typescript
 * const error = new Error('exceeds maximum allowed size of 5MB');
 * const restrictions = { maxFileSize: 5 * 1024 * 1024 };
 * const message = formatRestrictionError(error, restrictions);
 * // Returns: "File too large (max. 5 MB)"
 * ```
 */
export default function formatRestrictionError(
  error: Error,
  restrictions: UploaderRestrictions = {},
): string {
  const errorMessage = error.message?.toLowerCase() || '';

  // file type restriction
  if (errorMessage.includes('you can only upload:')
      || errorMessage.includes('allowed file types')
      || errorMessage.includes('not allowed')) {
    return 'Unsupported format.';
  }

  // total file size restriction (must check FIRST before individual file size)
  if (errorMessage.includes('total file size')
      || errorMessage.includes('total size')) {
    const maxSize = restrictions.maxTotalFileSize;
    if (maxSize) {
      const formattedSize = filesize(maxSize);
      return `Total size too large (max. ${formattedSize})`;
    }
    // If no maxTotalFileSize is set, fall through to check other restrictions
  }

  // max file size restriction - check AFTER total size to avoid ambiguous matches
  if ((errorMessage.includes('exceeds maximum allowed size')
      || errorMessage.includes('too large')
      || (errorMessage.includes('file size') && errorMessage.includes('exceeds')))
      && !errorMessage.includes('total')) { // Make sure it's not a total size error
    const maxSize = restrictions.maxFileSize || DEFAULT_MAX_FILE_SIZE;
    const formattedSize = filesize(maxSize);
    return `File too large (max. ${formattedSize})`;
  }

  // max number of files restriction
  if (errorMessage.includes('you can only upload')
      || errorMessage.includes('cannot upload')
      || (errorMessage.includes('file') && errorMessage.includes('maximum'))) {
    const maxFiles = restrictions.maxNumberOfFiles || DEFAULT_MAX_NUMBER_OF_FILES;
    return `${maxFiles} file maximum.`;
  }

  // min file size restriction
  if (errorMessage.includes('smaller than the allowed size')
      || errorMessage.includes('too small')) {
    const minSize = restrictions.minFileSize;
    if (minSize) {
      const formattedSize = filesize(minSize);
      return `File too small (min. ${formattedSize})`;
    }
  }

  // Fallback for unknown restriction errors
  return 'File restriction error.';
}
